import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import logoWhite from '../assets/logo-repro-white.svg';
import logoDark from '../assets/logo-repro-v3-dark.svg';
import { useAuth } from '../contexts/AuthProvider';
import { useTheme } from '../contexts/ThemeProvider';
import { SettingsDrawer } from './SettingsDrawer';

interface HeaderProps {
  mobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
}

const APIStatusIndicator: React.FC = () => {
  const [status, setStatus] = useState<'online' | 'offline' | 'checking'>('checking');

  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const check = async () => {
      try {
        const res = await fetch(`${apiBase}/health`);
        setStatus(res.ok ? 'online' : 'offline');
      } catch {
        setStatus('offline');
      }
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, []);

  const color =
    status === 'online' ? 'bg-success' : status === 'offline' ? 'bg-error' : 'bg-warning';
  const label =
    status === 'online' ? 'API Online' : status === 'offline' ? 'API Offline' : 'Checking...';

  return (
    <div className="flex items-center gap-1.5 text-xs">
      <div className={`w-2 h-2 rounded-full ${color}`} />
      <span className="text-base-content/60 font-medium">{label}</span>
    </div>
  );
};

export const Header: React.FC<HeaderProps> = ({ mobileMenuOpen, onToggleMobileMenu }) => {
  const { user, isSiteAdmin, logout } = useAuth();
  const { resolvedTheme } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleSignOut = async () => {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      setDrawerOpen(false);
    }
  };

  const roleLabel = isSiteAdmin ? 'Site Admin' : 'Org Admin';
  const logo = resolvedTheme === 'dark' ? logoWhite : logoDark;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-[60px] bg-base-200 border-b border-base-300">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left: mobile menu button */}
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleMobileMenu}
            className="btn btn-ghost btn-sm btn-square lg:hidden"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
          </button>
        </div>

        {/* Center: logo */}
        <Link
          to="/dashboard"
          className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2"
        >
          <img src={logo} alt="RePro" className="h-[30px] w-auto" />
          <span className="hidden sm:inline text-sm font-semibold text-base-content/80">
            Billing
          </span>
        </Link>

        {/* Right: API status + user menu trigger */}
        <div className="flex items-center gap-3">
          <div className="hidden sm:block">
            <APIStatusIndicator />
          </div>

          {user && (
            <button
              onClick={() => setDrawerOpen(true)}
              className="btn btn-ghost btn-sm gap-2"
              aria-label="Open settings menu"
              aria-expanded={drawerOpen}
              aria-haspopup="dialog"
            >
              <UserCircleIcon className="h-5 w-5" />
              <span className="hidden md:inline text-sm text-base-content/80 truncate max-w-[120px]">
                {user.name || user.email}
              </span>
            </button>
          )}
        </div>
      </div>

      {user && (
        <SettingsDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          userEmail={user.email}
          userName={user.name}
          roleLabel={roleLabel}
          onSignOut={handleSignOut}
          signingOut={loggingOut}
        />
      )}
    </header>
  );
};
